import { CSSProperties, useEffect, useRef } from 'react';

import { FOCO_VISIVEL } from 'utils/foco';

import { ART_HEIGHT_PX, ART_WIDTH_PX, IMAGE_BAND_MIN_HEIGHT_PX } from '../cardArt';
import { CartaoDeExperiencia as Cartao } from '../catalogo-modelo';
import RegrasDeUso from './RegrasDeUso';

interface CartaoDeExperienciaProps {
  cartao: Cartao;
  virado: boolean;
  onVirar: () => void;
  onDesvirar: () => void;
  onConfirmar: () => void;
  onPromocoesLivelo: () => void;
  /** A experiência resgatada no ciclo: selo "Sua escolha" e nenhum botão de resgate (TRC-07). */
  escolhido?: boolean;
  /** Período encerrado: só as regras, sem resgatar (TRC-20). */
  somenteConsulta?: boolean;
}

// O design vira o cartão em duas metades: a face que sai encolhe em 260ms e só então a outra
// cresce. A visibilidade troca na hora para a face que entra (para receber o foco já) e no fim
// para a que sai; sem isso o verso escondido seguiria alcançável por Tab e leitor de tela.
const ENTRANDO: CSSProperties = { transition: 'transform .26s cubic-bezier(.4,0,.2,1) .26s, opacity .16s linear .26s, visibility 0s' };
const SAINDO: CSSProperties = { transition: 'transform .26s cubic-bezier(.4,0,.2,1), opacity .16s linear, visibility 0s linear .26s' };
const FACE = 'absolute inset-0 flex flex-col overflow-hidden rounded-xl bg-brand-white motion-reduce:!transition-none';

// A face corta o que passa da borda (`overflow-hidden`), então o anel dos elementos colados nela
// vai para dentro. No azul do "Confirmo…" o anel é branco (4,88:1); o azul-escuro daria 1,83:1.
const FOCO_INTERNO =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-brand-blue-dark';
const FOCO_INTERNO_SOBRE_AZUL =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-brand-white';

// `inert` tira a face escondida do Tab e da árvore de acessibilidade (o React 18 não o conhece como prop).
const inerte = (ativo: boolean) => (ativo ? ({ inert: '' } as Record<string, string>) : {});

export const TIPO_ROTULO = { unico: 'único', combo: 'combo' } as const;

// Tarja de tipo: "único" em branco sobre o azul (4,88:1); "combo" em azul-escuro sobre o lilás (4,81:1).
export const TIPO_ESTILO = {
  unico: 'bg-brand-blue text-brand-white',
  combo: 'bg-brand-purple-light text-brand-blue-dark',
} as const;

export function TarjaDeTipo({ tipo }: { tipo: Cartao['tipo'] }) {
  return (
    <span
      className={`inline-block rounded-full px-[13px] py-1 font-text text-[10px] font-bold uppercase tracking-[0.06em] ${TIPO_ESTILO[tipo]}`}
    >
      {TIPO_ROTULO[tipo]}
    </span>
  );
}

/** Faixa da arte com a tarja por cima; sem arte, só a tarja, no topo do conteúdo. */
export function ArteDoCartao({ tipo, imagem }: Pick<Cartao, 'tipo' | 'imagem'>) {
  if (!imagem) {
    return (
      <div className="shrink-0 px-5 pt-[18px]">
        <TarjaDeTipo tipo={tipo} />
      </div>
    );
  }
  return (
    <div
      className="relative shrink-0 overflow-hidden bg-[#EEF1FF]"
      style={{ aspectRatio: `${ART_WIDTH_PX} / ${ART_HEIGHT_PX}`, minHeight: IMAGE_BAND_MIN_HEIGHT_PX }}
    >
      <img
        src={imagem}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="block h-full w-full object-contain transition-transform duration-300 group-hover/cena:scale-[1.045] motion-reduce:transition-none motion-reduce:group-hover/cena:scale-100"
      />
      <span className="absolute left-3 top-3">
        <TarjaDeTipo tipo={tipo} />
      </span>
    </div>
  );
}

/**
 * Cartão da vitrine (CAT-09, CAT-10): frente com arte, tarja, título, tagline, descrição, avisos e
 * "Quero viver essa experiência"; verso com as regras de uso e a confirmação. O escolhido e o de
 * consulta trocam o botão da frente por "Ver regras de uso" e ficam sem a confirmação (TRC-07, TRC-20).
 */
export default function CartaoDeExperiencia({
  cartao,
  virado,
  onVirar,
  onDesvirar,
  onConfirmar,
  onPromocoesLivelo,
  escolhido = false,
  somenteConsulta = false,
}: CartaoDeExperienciaProps) {
  const botaoDaFrente = useRef<HTMLButtonElement>(null);
  const tituloDoVerso = useRef<HTMLHeadingElement>(null);
  const verso = useRef<HTMLElement>(null);
  const devolverFoco = useRef(false);
  const tituloId = `experiencia-${cartao.id}-titulo`;
  const podeResgatar = !escolhido && !somenteConsulta;
  // A escolha já está garantida: vagas e "esgotada" do estoque ao vivo não valem para ela (VTR-14).
  const mostraEstoque = !escolhido;

  // No verso, o foco vai para o título. Na volta, vai para o botão que virou — só quando o próprio
  // jogador desvirou o cartão (✕ ou Esc). Se outro código o desvirou (o jogador virou outro cartão,
  // trocou o filtro), o foco fica onde o jogador está.
  useEffect(() => {
    if (virado) {
      tituloDoVerso.current?.focus();
    } else if (devolverFoco.current) {
      devolverFoco.current = false;
      botaoDaFrente.current?.focus();
    }
  }, [virado]);

  // O botão da frente ainda é `inert` neste instante: o foco volta no efeito, depois da troca de face.
  const voltarParaAFrente = () => {
    devolverFoco.current = true;
    onDesvirar();
  };

  // Esc no verso volta para a frente. O ouvinte é nativo porque o verso é um `article`, e o
  // jsx-a11y recusa handler de teclado em elemento não interativo; os controles continuam os botões.
  useEffect(() => {
    const elemento = verso.current;
    if (!elemento) return;
    const aoTeclarNoVerso = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return;
      evento.stopPropagation();
      devolverFoco.current = true;
      onDesvirar();
    };
    elemento.addEventListener('keydown', aoTeclarNoVerso);
    return () => elemento.removeEventListener('keydown', aoTeclarNoVerso);
  }, [onDesvirar]);

  return (
    <div className="group/cena relative h-[556px] transition-transform duration-200 hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <article
        aria-labelledby={tituloId}
        className={`${FACE} shadow-jj-01 transition-shadow group-hover/cena:shadow-[0_14px_30px_rgba(0,45,75,0.14)] ${virado ? 'invisible scale-x-0 opacity-0' : 'visible scale-x-100 opacity-100'}`}
        style={virado ? SAINDO : ENTRANDO}
        {...inerte(virado)}
      >
        <ArteDoCartao tipo={cartao.tipo} imagem={cartao.imagem} />
        <div tabIndex={0} role="group" aria-labelledby={tituloId} className={`min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-[18px] ${FOCO_INTERNO}`}>
          {escolhido && (
            <p className="m-0 mb-2.5">
              <span className="inline-block rounded-md bg-brand-blue-dark px-[9px] py-1 font-text text-[12px] font-bold text-brand-yellow">Sua escolha</span>
            </p>
          )}
          <h3 id={tituloId} className="m-0 font-display text-[20px] font-extrabold leading-[1.15] text-brand-blue-dark">
            {cartao.titulo}
          </h3>
          {cartao.tagline && (
            <p className="m-0 mt-3 flex items-start gap-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="var(--brand-yellow)" stroke="none" aria-hidden="true" focusable="false" className="mt-px shrink-0">
                <path d="m12 2 2.9 6.3 6.9.8-5 4.8 1.3 6.8L12 17.5 5.9 20.7 7.2 13.9l-5-4.8 6.9-.8z" />
              </svg>
              <span className="text-[12px] font-bold leading-[1.45] text-brand-blue">{cartao.tagline}</span>
            </p>
          )}
          {cartao.descricao && <p className="m-0 mt-3 text-[13px] leading-[1.6] text-content">{cartao.descricao}</p>}
          {cartao.aviso && (
            <p role="note" className="m-0 mt-3 rounded-lg bg-brand-yellow-light px-3 py-2 text-[12px] leading-[1.5] text-content">
              {cartao.aviso}
            </p>
          )}
          {mostraEstoque && typeof cartao.vagasRestantes === 'number' && (
            <p className="m-0 mt-3 flex items-center gap-[7px] text-[11px] text-content-soft">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--brand-blue)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="shrink-0">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 16v-4" />
                <path d="M12 8h.01" />
              </svg>
              <span>Quantidade de vagas disponíveis: {cartao.vagasRestantes}</span>
            </p>
          )}
          {cartao.proximaData && <p className="m-0 mt-3 text-[12px] leading-[1.5] text-content-soft">{cartao.proximaData}</p>}
          {mostraEstoque && cartao.esgotada && <p className="m-0 mt-3 text-[12px] font-bold text-content">Experiência esgotada</p>}
          {cartao.promocoesLivelo && (
            <button
              type="button"
              onClick={onPromocoesLivelo}
              aria-haspopup="dialog"
              className={`mt-3.5 flex w-full cursor-pointer items-center gap-[11px] rounded-lg border border-solid border-[#FF4FA3] bg-brand-white px-[13px] py-2.5 text-left transition-colors hover:bg-[#FFF5FA] ${FOCO_VISIVEL}`}
            >
              <span aria-hidden="true" className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#FF4FA3] font-display text-[16px] font-extrabold text-brand-white">
                L
              </span>
              {/* SPEC_DEVIATION: texto em #D6006E (design #FF4FA3, 3,04:1 sobre branco). */}
              <span className="text-[12px] font-bold leading-[1.4] text-[#D6006E]">Clique e confira as promoções da Livelo!</span>
            </button>
          )}
        </div>
        <button
          ref={botaoDaFrente}
          type="button"
          onClick={onVirar}
          disabled={podeResgatar && cartao.esgotada}
          className={`shrink-0 border-0 border-t border-dashed border-surface-200 px-3 py-[15px] font-text text-[12px] font-bold uppercase tracking-[0.04em] transition-colors ${
            podeResgatar && cartao.esgotada
              ? 'cursor-not-allowed bg-surface-200 text-content-soft'
              : 'cursor-pointer bg-brand-yellow text-brand-blue-dark hover:bg-button-primary-hover'
          } ${FOCO_INTERNO}`}
        >
          {/* O título distingue os botões de cartões diferentes na lista de botões do leitor de tela. */}
          {podeResgatar ? 'Quero viver essa experiência' : 'Ver regras de uso'}
          <span className="sr-only">: {cartao.titulo}</span>
        </button>
      </article>

      <article
        ref={verso}
        aria-label={`Regras de uso: ${cartao.tituloDoItem}`}
        className={`${FACE} border-2 border-solid border-brand-blue shadow-[0_14px_30px_rgba(0,45,75,0.14)] ${virado ? 'visible scale-x-100 opacity-100' : 'invisible scale-x-0 opacity-0'}`}
        style={virado ? ENTRANDO : SAINDO}
        {...inerte(!virado)}
      >
        <div className="relative shrink-0 px-5 pb-1 pt-[18px]">
          <button
            type="button"
            onClick={voltarParaAFrente}
            aria-label="Voltar para frente do card"
            className={`absolute right-3 top-3 flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-[15px] font-bold text-brand-blue-dark hover:bg-surface-100 ${FOCO_VISIVEL}`}
          >
            <span aria-hidden="true">✕</span>
          </button>
          <span className="inline-block rounded-md bg-brand-yellow px-[9px] py-1 font-text text-[12px] font-bold text-brand-blue-dark">
            Regras de uso:
          </span>
          <h4
            ref={tituloDoVerso}
            tabIndex={-1}
            className={`m-0 mt-3 pr-6 font-display text-[15px] font-bold leading-[1.25] text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            {cartao.tituloDoItem}
          </h4>
        </div>
        <div tabIndex={0} role="region" aria-label="Regras de uso da experiência" className={`min-h-0 flex-1 overflow-y-auto px-5 pb-[18px] pt-3.5 ${FOCO_INTERNO}`}>
          <RegrasDeUso paginas={cartao.regras} />
        </div>
        {podeResgatar && (
          <button
            type="button"
            onClick={onConfirmar}
            disabled={cartao.esgotada}
            className={`shrink-0 border-0 px-3 py-[15px] font-text text-[12px] font-bold uppercase tracking-[0.04em] transition-colors ${
              cartao.esgotada
                ? 'cursor-not-allowed bg-surface-200 text-content-soft'
                : // SPEC_DEVIATION: hover `brand-blue-dark` (design #5168FF, 4,40:1 com o branco).
                  'cursor-pointer bg-brand-blue text-brand-white hover:bg-brand-blue-dark'
            } ${FOCO_INTERNO_SOBRE_AZUL}`}
          >
            {cartao.esgotada ? (
              'Experiência esgotada'
            ) : (
              <>
                Confirmo que quero viver essa experiência<span className="sr-only">: {cartao.tituloDoItem}</span>
              </>
            )}
          </button>
        )}
      </article>
    </div>
  );
}
